/**
 * Metal Memory (v14) : vente additionnelle « entretoises murales » dans le panier (page et tiroir).
 *
 * Élément <mm-cart-crosssell> rendu par snippets/mm-cart-crosssell.liquid.
 * - le choix de la finition (Argent / Noir / Or) change la photo et le prix affichés ;
 * - « Ajouter » envoie la variante choisie (quantité 1) à l'API Ajax /cart/add.js, puis prévient le thème avec
 *   CartLinesUpdateEvent (même mécanisme que le bouton « Ajouter au panier » du thème) : la page panier et le tiroir
 *   se mettent à jour, et le bloc disparaît (un accessoire est alors dans le panier).
 * Aucune bibliothèque externe.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

class MmCartCrosssell extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = /** @type {HTMLInputElement | null} */ (event.target instanceof Element ? event.target.closest('.mm-xsell__radio') : null);
    if (!input || !input.checked) return;

    const price = this.querySelector('[data-mm-xsell-price]');
    if (price && input.dataset.price) price.textContent = input.dataset.price;

    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('.mm-xsell__image'));
    if (image && input.dataset.image) {
      image.removeAttribute('srcset');
      image.src = input.dataset.image;
      if (input.dataset.imageSrcset) image.srcset = input.dataset.imageSrcset;
      image.alt = input.dataset.imageAlt || '';
    }
    this.#showError('');
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-mm-xsell-add]') : null;
    if (!button) return;
    event.preventDefault();
    this.#add(/** @type {HTMLButtonElement} */ (button));
  };

  /** @param {string} message */
  #showError(message) {
    const el = this.querySelector('[data-mm-xsell-error]');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = message;
    el.hidden = !message;
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    if (this.#busy) return;
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('.mm-xsell__radio:checked:not(:disabled)'));
    if (!checked) return;

    this.#busy = true;
    button.disabled = true;
    this.#showError('');

    const variantId = checked.value;
    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');

    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    }
    if (sectionIds.length) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'application/json' },
      });
      const result = await response.json();

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const ajaxCart = await cartResponse.json();

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, source: 'mm-cart-crosssell', itemCount: 1 },
        });
        this.#showError(result.description || result.message || this.dataset.error || '');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'mm-cart-crosssell',
          itemCount: 1,
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.#showError(this.dataset.error || '');
    } finally {
      this.#busy = false;
      if (button.isConnected) button.disabled = false;
    }
  }
}

if (!customElements.get('mm-cart-crosssell')) customElements.define('mm-cart-crosssell', MmCartCrosssell);
